import { Building2, UserRound } from 'lucide-react';
import { DetailHero } from '@/components/common/DetailHero';
import { DetailInfoCard } from '@/components/common/DetailInfoCard';
import { DetailValue } from '@/components/common/DetailValue';
import { LabelWithValue } from '@/components/common/LabelWithValue';
import { PersonDetailLines } from '@/components/common/PersonDetailLines';
import { WorkTypeBadge } from '@/components/common/WorkTypeBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate, formatTime } from '@/utils/date';
import { OPTION_DETAIL_SEPARATOR } from '@/utils/filters';
import type { DcrEntryDetail } from '../types/billing.types';

interface DcrEntrySummaryProps {
  entry: DcrEntryDetail | undefined;
}

function getTimeRangeText(entry: DcrEntryDetail): string {
  return `${formatDisplayDate(entry.dcrDate)}${OPTION_DETAIL_SEPARATOR}${formatTime(entry.timeFrom)} – ${formatTime(entry.timeTo)}`;
}

function getRouteText(entry: DcrEntryDetail): string | null {
  if (!entry.areaFrom && !entry.areaTo) return null;
  return `${entry.areaFrom ?? EMPTY_VALUE} → ${entry.areaTo ?? EMPTY_VALUE}`;
}

export function DcrEntrySummary({ entry }: DcrEntrySummaryProps) {
  const isLoading = entry === undefined;
  const hasOnBehalf = Boolean(entry?.refZmName || entry?.refZmMobile);

  return (
    <div className="flex flex-col gap-4">
      <DetailHero
        heading={
          <div className="flex items-center gap-2.5">
            {entry ? (
              <>
                <WorkTypeBadge code={entry.workTypeCode} name={entry.workType} />
                <span className="text-lg font-semibold tracking-tight">
                  {entry.workType ?? EMPTY_VALUE}
                </span>
              </>
            ) : (
              <Skeleton className="h-7 w-52" />
            )}
          </div>
        }
        subline={
          <DetailValue isLoading={isLoading}>
            <span className="text-sm text-muted-foreground tabular-nums">
              {entry ? getTimeRangeText(entry) : null}
            </span>
          </DetailValue>
        }
        amountLabel="Claimed Fare"
        amount={entry?.fareAmount}
        amountNote={
          <DetailValue isLoading={isLoading}>
            <span className="text-xs text-muted-foreground">
              {entry?.transportType ?? EMPTY_VALUE}
            </span>
          </DetailValue>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <DetailInfoCard label="ZM" icon={UserRound}>
          <LabelWithValue label="ZM">
            <DetailValue isLoading={isLoading}>
              {entry ? (
                <PersonDetailLines name={entry.memberName} mobile={entry.memberMobile} />
              ) : null}
            </DetailValue>
          </LabelWithValue>
          {entry && hasOnBehalf ? (
            <LabelWithValue label="On Behalf Of">
              <PersonDetailLines name={entry.refZmName ?? EMPTY_VALUE} mobile={entry.refZmMobile} />
            </LabelWithValue>
          ) : null}
        </DetailInfoCard>

        <DetailInfoCard label="Visit" icon={Building2}>
          <LabelWithValue label="Institute">
            <DetailValue isLoading={isLoading}>{entry?.instituteName}</DetailValue>
          </LabelWithValue>
          <LabelWithValue label="Route">
            <DetailValue isLoading={isLoading}>{entry ? getRouteText(entry) : null}</DetailValue>
          </LabelWithValue>
        </DetailInfoCard>
      </div>
    </div>
  );
}
